import client from '../../../config/api';
import {
  LOCAL_SET_BUBBLES,
  LOCAL_REMOVE_BUBBLE,
  LOCAL_UPDATE_BUBBLE,
} from '../mutations/mutationTypes';

import { GET_WEBSOCKET_CONNECTION_ID } from '../../websocket/getters/getterTypes';

import { getRecord } from '../../../services/db';
import onlineService from '../../../services/onlineService';
import { CreateLatestDebouncedAction } from '../../../helpers/CreateLatestDebouncedAction';
import {
  ENTITY_TYPES,
  setSnapshot,
  setSnapshots,
  removeSnapshot,
  clearSnapshots,
  attachBase,
} from '../../../helpers/conflictTracker';

const getPath = (location, jobName, taskId) => {
  const site = location.substring(0, 3);
  return `uploads/sites/${site}/${jobName}/${taskId}`;
};

const handleBubbleFiles = async (dispatch, bubble, path) => {
  const uploadFilePromises = [];
  const isOnline = await onlineService.getStatus();

  if (bubble.images) {
    bubble.images.forEach((image, index) => {
      const imagePath = image.url || image.src || image.path;

      const isLocal =
        typeof imagePath === 'string' && imagePath.startsWith('data:');
      if (isLocal && isOnline) {
        const uploadFilePromise = fetch(imagePath)
          .then((fetchedUrl) => fetchedUrl.blob())
          .then((fetchedBlob) =>
            dispatch(
              'uploadFile',
              {
                file: fetchedBlob,
                path,
              },
              { root: true }
            )
          )
          .then((uploadUrl) => {
            bubble.images[index] = {
              name: image.name,
              description: image.description,
              url: uploadUrl,
            };
          });
        uploadFilePromises.push(uploadFilePromise);
      }
    });
  }

  return Promise.all(uploadFilePromises);
};

const handleBubblesFiles = async (dispatch, bubbles, path) => {
  const uploadFilesPromises = [];
  bubbles.forEach((bubble) => {
    uploadFilesPromises.push(handleBubbleFiles(dispatch, bubble, path));
  });

  return Promise.all(uploadFilesPromises);
};

export default {
  async SAVE_BUBBLES({ dispatch, rootState, rootGetters }, payload) {
    const { jobName, taskId, data } = payload;

    await handleBubblesFiles(
      dispatch,
      data,
      getPath(rootState.currentUser.location, jobName, taskId)
    );

    const clientWebSocketId = rootGetters[GET_WEBSOCKET_CONNECTION_ID];
    return client.post(
      `/bubbles/${taskId}?wsConnectionId=${clientWebSocketId}`,
      data
    );
  },
  async GET_BUBBLES({ commit }, taskId) {
    const result = await client.get(`/bubbles/${taskId}`);
    commit(LOCAL_SET_BUBBLES, result.data);
    clearSnapshots(ENTITY_TYPES.BUBBLE);
    setSnapshots(ENTITY_TYPES.BUBBLE, result.data);
    return result.data;
  },
  async DELETE_BUBBLE({ commit }, bubbleId) {
    if (!bubbleId) {
      return;
    }

    await client.delete(`/bubble/${bubbleId}`);
    commit(LOCAL_REMOVE_BUBBLE, bubbleId);
    removeSnapshot(ENTITY_TYPES.BUBBLE, bubbleId);
  },

  async DELETE_BUBBLES(_1, payload) {
    const taskId = payload;
    return client.delete(`/bubbles/${taskId}`);
  },
  async CREATE_BUBBLE({ dispatch, rootState, rootGetters }, data) {
    const { taskId, bubble, jobName } = data;
    if (!taskId || !bubble) {
      return;
    }

    await handleBubbleFiles(
      dispatch,
      bubble,
      getPath(rootState.currentUser.location, jobName, taskId)
    );

    const taskOfBubble = await getRecord('task', taskId);
    if (taskOfBubble) {
      const jobOfTask = await getRecord('job', taskOfBubble.jobId);

      if (jobOfTask && jobOfTask.checkedOut && jobOfTask.checkedOut !== '') {
        bubble.checkedJobId = jobOfTask._id;
      }
    }

    const clientWebSocketId = rootGetters[GET_WEBSOCKET_CONNECTION_ID];
    const response = await client.post(
      `/bubble/${taskId}?wsConnectionId=${clientWebSocketId}`,
      bubble
    );
    if (response && response.data) {
      setSnapshot(ENTITY_TYPES.BUBBLE, response.data);
    }
    return response.data;
  },

  UPDATE_BUBBLE: CreateLatestDebouncedAction({
    keyExtractor: (bubble) => bubble._id,
    delay: 500,
    handlerFn: async ({ context, payload }) => {
      const { dispatch, rootState, rootGetters } = context;

      await handleBubbleFiles(
        dispatch,
        payload,
        getPath(rootState.currentUser.location, payload.jobName, payload.taskId)
      );

      const clientWebSocketId = rootGetters[GET_WEBSOCKET_CONNECTION_ID];

      const requestBody = attachBase(ENTITY_TYPES.BUBBLE, payload);

      const res = await client.put(
        `/bubble/${payload._id}?wsConnectionId=${clientWebSocketId}`,
        requestBody
      );
      return res.data;
    },
    commitFn: (commit, bubble) => {
      commit(LOCAL_UPDATE_BUBBLE, bubble);
      if (bubble && bubble._id) {
        setSnapshot(ENTITY_TYPES.BUBBLE, bubble);
      }
    },
  }),
};
